import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { B2B_INQUIRY_PATH, LANDING_PATH, marketingMainNav } from './marketingNav'

function ArrowRightIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={className}>
      <path d="M4.5 10h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M11.5 6l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
      <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function navLinkClass(active) {
  return [
    'relative rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200',
    'after:pointer-events-none after:absolute after:inset-x-3 after:bottom-1 after:h-0.5 after:origin-center after:rounded-full after:bg-gradient-to-r after:from-brand-sky after:to-brand-mint after:transition-transform after:duration-300 motion-reduce:after:hidden',
    active
      ? 'text-brand-navy after:scale-x-100'
      : 'text-slate-600 after:scale-x-0 hover:bg-slate-100 hover:text-brand-navy hover:after:scale-x-100',
  ].join(' ')
}

/**
 * @param {{ currentPath?: string }} props — 생략 시 useLocation().pathname 사용
 */
export default function MarketingSiteHeader({ currentPath: currentPathProp }) {
  const location = useLocation()
  const pathname = currentPathProp ?? location.pathname
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    const onResize = () => {
      if (typeof window !== 'undefined' && window.innerWidth >= 768) setOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const close = () => setOpen(false)

  const isActive = (to) => {
    if (to === LANDING_PATH) return pathname === LANDING_PATH
    if (to === '/support') return pathname === '/support' || pathname.startsWith('/support/')
    return pathname === to || pathname.startsWith(`${to}/`)
  }

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 shadow-sm shadow-slate-200/50 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5">
        <Link
          to={LANDING_PATH}
          className="group flex shrink-0 items-baseline gap-2 motion-safe:transition-transform motion-safe:duration-200 motion-safe:active:scale-[0.98]"
          onClick={close}
        >
          <span className="text-2xl font-black tracking-tight text-brand-navy transition-colors duration-200 group-hover:text-sky-600">설계EZ</span>
          <span className="hidden text-[11px] font-semibold tracking-[0.2em] text-slate-400 sm:inline">SGEZ</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="주요 메뉴">
          {marketingMainNav.map((item) => (
            <Link key={item.to} to={item.to} className={navLinkClass(isActive(item.to))}>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Link
            className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:border-sky-200 hover:bg-sky-50 hover:text-brand-navy motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.98]"
            to={B2B_INQUIRY_PATH}
          >
            도입 상담
          </Link>
          <Link
            className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-brand-navy/25 transition-all duration-200 hover:bg-sky-700 motion-safe:hover:scale-[1.03] motion-safe:active:scale-[0.97]"
            to="/login"
          >
            시작하기
            <ArrowRightIcon />
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm transition-transform duration-200 motion-safe:active:scale-95"
            aria-expanded={open}
            aria-controls="marketing-mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="sr-only">{open ? '메뉴 닫기' : '메뉴 열기'}</span>
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {open ? (
        <div id="marketing-mobile-nav" className="origin-top border-t border-slate-100 bg-white lg:hidden">
          <div className="mx-auto max-w-6xl space-y-1 px-4 py-4">
            {marketingMainNav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block rounded-xl px-3 py-3 text-base font-semibold text-slate-800 hover:bg-slate-50"
                onClick={close}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-4 grid gap-2 border-t border-slate-100 pt-4">
              <Link
                className="block rounded-xl border border-slate-200 py-3 text-center text-sm font-bold text-brand-navy transition-transform duration-200 motion-safe:active:scale-[0.99]"
                to={B2B_INQUIRY_PATH}
                onClick={close}
              >
                도입 상담
              </Link>
              <Link
                className="block rounded-xl bg-brand-navy py-3 text-center text-sm font-bold text-white transition-transform duration-200 motion-safe:active:scale-[0.99]"
                to="/login"
                onClick={close}
              >
                시작하기
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  )
}
